﻿import { computed, Ref, ref, shallowRef, ShallowRef } from 'vue';
import { CodeEditContextRefCode, CodeEditContextRequest } from '@/shared/helpers/api.ts';
import { CodeEditSessionItem, RegexValidators } from '@/shared/codeedit/codeEditSessionItem.ts';
import { FieldDefinition } from '@/shared/codeedit/field.ts';
import { AttributeValue, codeEditFieldsDiff, CodeEditSessionMetadata } from '@/shared/codeedit/codeEditFields.ts';
import { EditSessionState, fetchCtx, newCodeDefaultId } from '@/shared/codeedit/editSessionState.ts';
import { CodeTemplateValues } from '@/shared/codeedit/codeTemplate.ts';
import { RelatedCode, RelatedCodeSet } from '@/shared/helpers/api/types/codeEditContextResponse.ts';
import { getReferenceCodeLabel } from '@/shared/helpers/referableCode.ts';
export class CodeEditSessionState {
    base: EditSessionState;

    code: Ref<CodeEditSessionItem | null>;
    /*
    We use a shallow ref to store the referable codes. This is because the object can be really large (fields with 10 000+ array elements), and makes the page unusable when vue dev-tools renders these objects.
     */
    referableCodes: ShallowRef<Record<string, CodeEditContextRefCode[]>>;
    relatedCodeSets: ShallowRef<RelatedCodeSet[]>;
    relatedCodes: ShallowRef<RelatedCode[]>;
    fieldDefs: Ref<FieldDefinition[]>;
    codeSetName: Ref<string | null>;
    regexValidators: Ref<RegexValidators>;
    libraryName: Ref<string | null>;
    codeId: Ref<number | null>;
    codeTemplateValues?: CodeTemplateValues;

    constructor(base?: EditSessionState) {
        if (base) {
            this.base = base;
        } else {
            this.base = new EditSessionState();
        }
        this.base.addClearChangesCallback(() => {
            this.applyTemplateValues();
        });
        this.base.addPostIdentityCheckCallback(() => this.updateAnchorIdentityOnRelationCodes());
        this.code = ref(null);
        this.referableCodes = shallowRef({});
        this.relatedCodeSets = shallowRef([]);
        this.relatedCodes = shallowRef([]);
        this.fieldDefs = ref([]);
        this.codeSetName = ref(null);
        this.regexValidators = ref({});
        this.libraryName = ref(null);
        this.codeId = ref(null);
    }

    setCodeTemplateValues(templateValues: CodeTemplateValues) {
        this.codeTemplateValues = templateValues;
    }

    applyTemplateValues() {
        if (this.codeTemplateValues && this.codeMetadata.value.isNew) {
            for (const key in this.codeTemplateValues) {
                const templateValue = this.codeTemplateValues[key];
                if (templateValue instanceof Function) {
                    this.setFieldValue(key, templateValue(), false);
                } else {
                    this.setFieldValue(key, templateValue, false);
                }
            }
        }
    }

    setCode(libName: string, csName: string, id: number) {
        this.code.value = this.base.collection.value[libName][csName][id];
        this.referableCodes.value = this.base.referableCodes.value[csName];
        this.fieldDefs.value = this.base.fieldDefs.value[libName];
        this.regexValidators.value = this.base.regexValidators.value[csName];
        this.libraryName.value = libName;
        this.codeSetName.value = csName;
        this.codeId.value = id;
    }

    codeMetadata = computed((): CodeEditSessionMetadata => {
        return {
            libraryName: this.libraryName.value ?? '',
            codeSetName: this.codeSetName.value ?? '',
            codeId: this.codeId.value ?? -1,
            isNew: (this.codeId.value ?? -1) >= newCodeDefaultId
        };
    });

    diff = computed(() => {
        if (this.code.value) {
            return codeEditFieldsDiff(this.code.value.modified, this.code.value.original, this.fieldDefs.value);
        }
        return undefined;
    });

    async loadFromContext(id: number, r: CodeEditContextRequest, newCode: boolean) {
        const ctx = await fetchCtx(r);
        this.base.reset();
        this.base.initCodeSet(ctx.library.name, ctx.codeSet);
        this.base.initializeFromEditContext(id, ctx, newCode);
        this.base.setReferableCodes(ctx.codeSet, ctx.referableCodes);
        this.setCode(ctx.library.name, ctx.codeSet, id);

        this.relatedCodeSets.value = ctx.relatedCodeSets;

        const promises: Promise<void>[] = [];

        for (const element of ctx.relatedCodeSets) {
            // relation libraries
            promises.push(fetchCtx({ codeSetName: element.relationLibCodeSetName }).then(refLibCtx => {
                this.base.initCodeSet(refLibCtx.library.name, refLibCtx.codeSet);
                const fieldDefinitions = this.base.initializeFieldDefinitions(refLibCtx.library, refLibCtx.dynamicFilterCaseSensitiveCodeRefs);
                this.base.initializeRegexValidators(
                    refLibCtx.codeSet,
                    refLibCtx.codeNameRegex,
                    refLibCtx.codeNameRegexErrorMessage,
                    refLibCtx.codeDescriptionRegex,
                    refLibCtx.codeDescriptionRegexErrorMessage);
                this.base.setValidators(refLibCtx.codeSet, fieldDefinitions);
                this.base.setReferableCodes(refLibCtx.codeSet, refLibCtx.referableCodes);
            }));
        }
        await Promise.all(promises);

        for (const relatedCode of ctx.relatedCodes) {
            const fieldDefs = this.base.fieldDefs.value[relatedCode.libraryName];
            if (!fieldDefs) {
                continue;
            }
            this.base.initializeCode(relatedCode.id, fieldDefs, relatedCode.libraryName, relatedCode.codeSetName, relatedCode);
        }
    }

    /**
     *
     * @param field
     * @param value
     * @param setIfNotExists Set the value even if the field does not exist on the code
     */
    setFieldValue(field: string, value: AttributeValue, setIfNotExists: boolean = true) {
        if (this.code.value) {
            if (!setIfNotExists && !Object.hasOwn(this.code.value.modified, field)) {
                return;
            }
            this.code.value.modified[field] = value;
        }
    }

    resetFieldValue(field: string) {
        if (this.code.value) {
            this.code.value.modified[field] = this.code.value.original[field];
        }
    }

    /**
     * Generates the name for a new relation library code.
     * Must stay in sync with the C# code:
     *   RelationLibraryCodeNameGenerator.GenerateName
     *   (Statoil.TI.CommonLibrary.Services/Batch/RelationLibraryCodeNameGenerator.cs)
     */
    static generateRelationLibCodeName(library1name: string, codeRef1Name: string, library2name: string, codeRef2Name: string): string {
        return `${library1name}-${codeRef1Name}_${library2name}-${codeRef2Name}`;
    }

    /**
     * Generates the description for a new relation library code.
     * Must stay in sync with the C# code:
     *   RelationLibraryCodeNameGenerator.GenerateDescription
     *   (Statoil.TI.CommonLibrary.Services/Batch/RelationLibraryCodeNameGenerator.cs)
     */
    static generateRelationLibCodeDesc(library1name: string, codeRef1Name: string, library2name: string, codeRef2Name: string): string {
        return `${library1name}-${codeRef1Name} relates to ${library2name}-${codeRef2Name}`;
    }

    private setCodeRefInRelationLibrary(
        fieldDefinitions: FieldDefinition[],
        relLibraryName: string,
        relLibCodeSetName: string,
        id: number,
        refLibraryName: string,
        value: AttributeValue
    ) {
        const targetFieldDef = fieldDefinitions.find(f => f.refLibraryName === refLibraryName);
        if (!targetFieldDef) {
            throw new Error('Could not find attribute definition referencing: ' + refLibraryName);
        }
        this.base.setFieldValue(relLibraryName, relLibCodeSetName, id, targetFieldDef.field, value);
    }

    private getAllRelationCodesFromCollection(codeSetName: string): CodeEditSessionItem[] {
        const relatedCodeSet = this.relatedCodeSets.value.find(rcs => rcs.relationLibCodeSetName === codeSetName);
        if (!relatedCodeSet) {
            return [];
        }
        const codeSetCollection = this.base.collection.value[relatedCodeSet.relationLibraryName]?.[codeSetName];
        return codeSetCollection ? Object.values(codeSetCollection) : [];
    }

    getRelationCodeDisplayLabel(code: CodeEditSessionItem, codeSetName: string): string {
        const relatedCodeSet = this.relatedCodeSets.value.find(rcs => rcs.relationLibCodeSetName === codeSetName);
        if (!relatedCodeSet) return String(code.modified.identity ?? '');

        const identity = String(code.modified[relatedCodeSet.relationLibAttributeName] ?? '');
        const refCode = this.base.getReferableCodeByIdentity(codeSetName, relatedCodeSet.relationLibAttributeName, identity);
        return refCode ? getReferenceCodeLabel(relatedCodeSet.referenceDisplayMode, refCode) : identity;
    }

    getSortedDisplayableRelationCodes(codeSetName: string): CodeEditSessionItem[] {
        return this.getAllRelationCodesFromCollection(codeSetName)
            .filter(item => item.modified.isValid || item.original.isValid)
            .sort((a, b) => this.getRelationCodeDisplayLabel(a, codeSetName).localeCompare(this.getRelationCodeDisplayLabel(b, codeSetName)));
    }

    getActiveRelationCodes(codeSetName: string): CodeEditSessionItem[] {
        return this.getAllRelationCodesFromCollection(codeSetName).filter(item => item.modified.isValid === true);
    }

    getAvailableCodeRefsForSelector(codeSetName: string, attributeName: string): CodeEditContextRefCode[] {
        const activeRelations = this.getActiveRelationCodes(codeSetName);
        const relatedIdentities = new Set(activeRelations.map(item => item.modified[attributeName]));
        return this.base.referableCodes.value[codeSetName][attributeName]
            .filter(rc => !relatedIdentities.has(rc.identity));
    }

    isAddedInSession(item: CodeEditSessionItem): boolean {
        return item.metadata.isNew || item.original.isValid === false;
    }

    isPendingRemoval(item: CodeEditSessionItem): boolean {
        return item.original.isValid === true && item.modified.isValid === false;
    }

    private getTargetFieldDef(fieldDefinitions: FieldDefinition[], targetLibraryName: string): FieldDefinition {
        const targetFieldDef = fieldDefinitions.find(f => f.refLibraryName === targetLibraryName);
        if (!targetFieldDef) {
            throw new Error('Could not find attribute definition referencing: ' + targetLibraryName);
        }
        return targetFieldDef;
    }

    addCodeToRelationLibrary(relLibraryName: string, relLibCodeSetName: string, targetIdentity: string, targetLibraryName: string) {
        const fieldDefinitions = this.base.fieldDefs.value[relLibraryName];
        const targetFieldDef = this.getTargetFieldDef(fieldDefinitions, targetLibraryName);

        // Check if an existing invalidated code with this target identity already exists in the collection
        const existingInvalidated = this.getAllRelationCodesFromCollection(relLibCodeSetName)
            .find(item => !item.modified.isValid && item.modified[targetFieldDef.field] === targetIdentity);

        if (existingInvalidated) {
            // Restore the existing code instead of creating a new one
            existingInvalidated.modified.isValid = true;
            return;
        }

        const id = this.base.nextNewTmpId();
        this.base.initializeNewCode(id, fieldDefinitions, relLibraryName, relLibCodeSetName);
        this.base.collection.value[relLibraryName][relLibCodeSetName][id].metadata.deleteOnClearChanges = true;

        // Set the target CodeRef
        this.setCodeRefInRelationLibrary(fieldDefinitions, relLibraryName, relLibCodeSetName, id, targetLibraryName, targetIdentity);

        // Set anchor identity, name and description via the shared update method
        this.updateAnchorIdentityOnRelationCodes();
    }

    /**
     * Updates the anchor CodeRef, name, and description on all relation codes that were added in this session
     * to reflect the current identity of the code being created.
     * Should be called whenever the code's identity or name changes.
     */
    updateAnchorIdentityOnRelationCodes() {
        if (!this.code.value) return;
        const anchorIdentity = this.code.value.modified.identity || '';
        const anchorLibraryName = this.code.value.metadata.libraryName;
        const anchorCodeRefName = this.code.value.modified.name || anchorIdentity;

        for (const relatedCodeSet of this.relatedCodeSets.value) {
            const fieldDefinitions = this.base.fieldDefs.value[relatedCodeSet.relationLibraryName];
            if (!fieldDefinitions) continue;

            const targetFieldDef = fieldDefinitions.find(f => f.refLibraryName === relatedCodeSet.targetLibraryName);
            if (!targetFieldDef) continue;

            for (const code of this.getAllRelationCodesFromCollection(relatedCodeSet.relationLibCodeSetName)) {
                if (!code.metadata.isNew) continue;

                this.setCodeRefInRelationLibrary(fieldDefinitions, relatedCodeSet.relationLibraryName, relatedCodeSet.relationLibCodeSetName, code.metadata.codeId, anchorLibraryName, anchorIdentity);

                const targetIdentity = String(code.modified[targetFieldDef.field] ?? '');
                const targetCodeRef = this.base.getReferableCodeByIdentity(relatedCodeSet.relationLibCodeSetName, targetFieldDef.field, targetIdentity);
                const targetCodeRefName = targetCodeRef?.name ?? '';
                const name = CodeEditSessionState.generateRelationLibCodeName(anchorLibraryName, anchorCodeRefName, relatedCodeSet.targetLibraryName, targetCodeRefName);
                const desc = CodeEditSessionState.generateRelationLibCodeDesc(anchorLibraryName, anchorCodeRefName, relatedCodeSet.targetLibraryName, targetCodeRefName);
                this.base.setFieldValue(relatedCodeSet.relationLibraryName, relatedCodeSet.relationLibCodeSetName, code.metadata.codeId, 'name', name);
                this.base.setFieldValue(relatedCodeSet.relationLibraryName, relatedCodeSet.relationLibCodeSetName, code.metadata.codeId, 'description', desc);
            }
        }
    }

    removeRelationCode(relatedCode: CodeEditSessionItem, relLibraryName: string, relLibCodeSetName: string) {
        if (relatedCode.metadata.isNew) {
            // Added in session — remove from collection
            delete this.base.collection.value[relLibraryName][relLibCodeSetName][relatedCode.metadata.codeId];
        } else {
            // Existed before — mark as pending removal
            relatedCode.modified.isValid = false;
        }
    }
}
